import {
  ChatAlt2Icon,
  CheckCircleIcon,
  ClockIcon,
  CodeIcon,
  CubeIcon,
  CurrencyDollarIcon,
  CursorClickIcon,
  DatabaseIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  HashtagIcon,
  IdentificationIcon,
  LinkIcon,
  ServerIcon,
  SwitchHorizontalIcon,
} from "@heroicons/react/outline";

import {
  ARRAY_OPERATORS,
  BOOLEAN_OPERATORS,
  ID_OPERATORS,
  NESTED_OPERATORS,
  NUMERIC_OPERATORS,
  STATUS_OPERATORS,
  STRING_OPERATORS,
  TEXT_SEARCH_OPERATORS,
} from "../config/constants";
import {
  FilterValueType,
  GlobalSearchControlType,
  SuggestionSourceType,
  ValueDisplayType,
  type FilterDefinition,
  type FilterEnumOption,
  type StructuredSearchSchema,
  type StructuredSearchState,
} from "../config/structured-search-schema";
import { VersionedEntityKind, type VersionedEntityConfig } from "../config/versioned-entity-config";
import {
  buildVersionRef,
  getVersionedEntityId,
  getVersionedEntityVersionNumber,
  isVersionedEntityFilterValue,
  isVersionedEntityFilterValueList,
  parseVersionedEntityFilterValue,
} from "../config/versioned-entity-utils";
import { TraceVersionSuggestionField } from "../config/types";
import type {
  Filter,
  StructuredFilterGroupRequest,
  StructuredFilterRequest,
  VersionedEntityFilterValue,
} from "../config/types";

const ICON_CLASS = "bg-gray-100 text-gray-500";

// "Span is Root" has no backend field — cast to span_depth == 1 (root).
const SPAN_IS_ROOT_FIELD = "span_is_root";
const SPAN_DEPTH_FIELD = "span_depth";
const ROOT_SPAN_DEPTH = 1;

// "PromptLayer Span" compiles to a span_attributes.node_type nested query.
const SPAN_ATTRIBUTES_FIELD = "span_attributes";
const SPAN_NODE_TYPE_KEY = "node_type";
const SPAN_TYPE_OPERATOR_TO_NESTED: Record<string, string> = {
  is: "key_equals",
  is_not: "key_not_equals",
  in: "in",
  not_in: "not_in",
};

// "Span Input" / "Span Output" reuse span-attribute nested search on
// function_input / function_output sub-keys.
const SPAN_INPUT_FIELD = "span_input";
const SPAN_OUTPUT_FIELD = "span_output";
const SPAN_INPUT_ATTR_PREFIX = "function_input";
const SPAN_OUTPUT_ATTR_PREFIX = "function_output";
const TRACE_DATE_RANGE_FIELD = "trace_start";

export const SPAN_TYPE_FIELD = "span_type";

const SPAN_NODE_TYPE_OPTIONS: FilterEnumOption[] = [
  { value: "WORKFLOW", label: "Workflow" },
  { value: "PROMPT_TEMPLATE", label: "Prompt Template" },
  { value: "TOOL_EXECUTION", label: "Tool" },
  { value: "LLM_SESSION", label: "LLM Session" },
  { value: "CODE_EXECUTION", label: "Code Execution" },
];

const TRACE_STATUS_OPTIONS: FilterEnumOption[] = [
  { value: "success", label: "Success" },
  { value: "error", label: "Error" },
];

const TRACE_PROMPT_VERSIONED_ENTITY = {
  kind: VersionedEntityKind.Prompt,
  versionRefField: "trace_prompt_version_refs",
  versionsSuggestionField: TraceVersionSuggestionField.TracePromptVersions,
} as const;

const TRACE_WORKFLOW_VERSIONED_ENTITY = {
  kind: VersionedEntityKind.Workflow,
  versionRefField: "trace_workflow_version_refs",
  versionsSuggestionField: TraceVersionSuggestionField.TraceWorkflowVersions,
} as const;

const SPAN_PROMPT_VERSIONED_ENTITY = {
  kind: VersionedEntityKind.Prompt,
  versionRefField: "span_prompt_version_ref",
  versionsSuggestionField: TraceVersionSuggestionField.SpanPromptVersions,
} as const;

const SPAN_WORKFLOW_VERSIONED_ENTITY = {
  kind: VersionedEntityKind.Workflow,
  versionRefField: "span_workflow_version_ref",
  versionsSuggestionField: TraceVersionSuggestionField.SpanWorkflowVersions,
} as const;

// const SUBTREE_PROMPT_VERSIONED_ENTITY = {
//   kind: VersionedEntityKind.Prompt,
//   versionRefField: "subtree_prompt_version_refs",
//   versionsSuggestionField: TraceVersionSuggestionField.SubtreePromptVersions,
// } as const;

// const SUBTREE_WORKFLOW_VERSIONED_ENTITY = {
//   kind: VersionedEntityKind.Workflow,
//   versionRefField: "subtree_workflow_version_refs",
//   versionsSuggestionField: TraceVersionSuggestionField.SubtreeWorkflowVersions,
// } as const;

const TRACE_FILTERS: FilterDefinition[] = [
  // ---- Trace-level (matches an entire trace) ----
  {
    key: "trace_name",
    label: "Trace Name",
    type: FilterValueType.String,
    operators: STRING_OPERATORS,
    group: "Trace",
    aliases: ["name", "trace name"],
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Root span name of the trace",
    suggestions: { source: SuggestionSourceType.Aggregation },
  },
  {
    key: "trace_status",
    label: "Trace Status",
    type: FilterValueType.Enum,
    operators: STATUS_OPERATORS,
    options: TRACE_STATUS_OPTIONS,
    valueDisplay: ValueDisplayType.TraceStatus,
    suggestions: { source: SuggestionSourceType.Aggregation },
    group: "Trace",
    aliases: ["status", "trace status"],
    icon: CheckCircleIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the trace succeeded or errored",
  },
  {
    key: "trace_id",
    label: "Trace ID",
    type: FilterValueType.String,
    operators: ID_OPERATORS,
    group: "Trace",
    aliases: ["id", "trace id"],
    icon: IdentificationIcon,
    iconClassName: ICON_CLASS,
    description: "Unique identifier for the trace",
  },
  {
    key: "trace_run_by_user_id",
    label: "Trace User ID",
    type: FilterValueType.String,
    operators: ID_OPERATORS,
    group: "Trace",
    aliases: ["user", "user id"],
    suggestions: { source: SuggestionSourceType.Aggregation },
    icon: IdentificationIcon,
    iconClassName: ICON_CLASS,
    description: "User associated with the trace",
  },
  {
    key: "trace_duration_ms",
    label: "Trace Duration",
    type: FilterValueType.Latency,
    operators: NUMERIC_OPERATORS,
    unit: "ms",
    group: "Trace",
    aliases: ["duration", "latency", "time"],
    icon: ClockIcon,
    iconClassName: ICON_CLASS,
    description: "End-to-end duration of the trace",
  },
  {
    key: "trace_total_cost_usd",
    label: "Trace Cost",
    type: FilterValueType.Cost,
    operators: NUMERIC_OPERATORS,
    unit: "$",
    group: "Trace",
    aliases: ["cost", "price", "spend"],
    icon: CurrencyDollarIcon,
    iconClassName: ICON_CLASS,
    description: "Total estimated cost of the trace",
  },
  {
    key: "trace_total_tokens",
    label: "Trace Total Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    unit: "tokens",
    group: "Trace",
    aliases: ["tokens", "total tokens"],
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Total tokens across all spans",
  },
  {
    key: "trace_input_tokens",
    label: "Trace Input Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    unit: "tokens",
    group: "Trace",
    aliases: ["input tokens"],
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Input tokens across all spans",
  },
  {
    key: "trace_output_tokens",
    label: "Trace Output Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    unit: "tokens",
    group: "Trace",
    aliases: ["output tokens"],
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Output tokens across all spans",
  },
  {
    key: "trace_span_count",
    label: "# of Spans",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Trace",
    aliases: ["spans", "# of spans"],
    icon: SwitchHorizontalIcon,
    iconClassName: ICON_CLASS,
    description: "Number of spans in the trace",
  },
  {
    key: "trace_depth",
    label: "Trace Depth",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Trace",
    aliases: ["depth"],
    icon: SwitchHorizontalIcon,
    iconClassName: ICON_CLASS,
    description: "Maximum span nesting depth of the trace",
  },
  {
    key: "trace_models_used",
    label: "Models Used in Trace",
    type: FilterValueType.String,
    operators: ARRAY_OPERATORS,
    group: "Trace",
    aliases: ["model", "models", "trace model", "models used in trace"],
    suggestions: { source: SuggestionSourceType.Aggregation },
    icon: CubeIcon,
    iconClassName: ICON_CLASS,
    description: "Models used anywhere in the trace",
  },
  {
    key: "trace_tool_names",
    label: "Tools Used in Trace",
    type: FilterValueType.String,
    operators: ARRAY_OPERATORS,
    group: "Trace",
    aliases: ["tool", "tools", "tool name"],
    suggestions: { source: SuggestionSourceType.Aggregation },
    icon: CursorClickIcon,
    iconClassName: ICON_CLASS,
    description: "Names of tools used across the trace",
  },
  {
    key: "trace_prompt_ids",
    label: "Prompt Templates Used in Trace",
    type: FilterValueType.Enum,
    operators: ARRAY_OPERATORS,
    group: "Trace",
    aliases: ["prompt", "prompt id"],
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Prompt templates used across the trace",
    suggestions: { source: SuggestionSourceType.Aggregation },
    versionedEntity: TRACE_PROMPT_VERSIONED_ENTITY,
  },
  {
    key: "trace_workflow_ids",
    label: "Workflows Used in Trace",
    type: FilterValueType.Enum,
    operators: ARRAY_OPERATORS,
    group: "Trace",
    aliases: ["workflow", "workflow id"],
    icon: SwitchHorizontalIcon,
    iconClassName: ICON_CLASS,
    description: "Workflows used across the trace",
    suggestions: { source: SuggestionSourceType.Aggregation },
    versionedEntity: TRACE_WORKFLOW_VERSIONED_ENTITY,
  },

  // ---- Span-level (span attribute search) ----
  {
    key: "span_name",
    label: "Span Name",
    type: FilterValueType.String,
    operators: STRING_OPERATORS,
    group: "Span",
    aliases: ["span name"],
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Name of an individual span",
    suggestions: { source: SuggestionSourceType.Aggregation },
  },
  {
    key: SPAN_TYPE_FIELD,
    label: "PromptLayer Span Type",
    type: FilterValueType.Enum,
    operators: STATUS_OPERATORS,
    group: "Span",
    icon: CubeIcon,
    iconClassName: ICON_CLASS,
    description: "PromptLayer span type (workflow / prompt / tool)",
    options: SPAN_NODE_TYPE_OPTIONS,
    suggestions: { source: SuggestionSourceType.Static },
  },
  {
    key: "span_kind",
    label: "OTEL Span Kind",
    type: FilterValueType.String,
    operators: STATUS_OPERATORS,
    group: "Span",
    aliases: ["otel span", "span kind", "kind"],
    suggestions: { source: SuggestionSourceType.Aggregation },
    icon: CodeIcon,
    iconClassName: ICON_CLASS,
    description: "OpenTelemetry SpanKind (e.g. INTERNAL, CLIENT)",
  },
  {
    key: "span_status",
    label: "Span Status",
    type: FilterValueType.Enum,
    operators: STATUS_OPERATORS,
    options: TRACE_STATUS_OPTIONS,
    valueDisplay: ValueDisplayType.TraceStatus,
    suggestions: { source: SuggestionSourceType.Aggregation },
    group: "Span",
    aliases: ["span status"],
    icon: CheckCircleIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the span succeeded or errored",
  },
  {
    key: "span_id",
    label: "Span ID",
    type: FilterValueType.String,
    operators: ID_OPERATORS,
    group: "Span",
    aliases: ["span id"],
    icon: IdentificationIcon,
    iconClassName: ICON_CLASS,
    description: "Unique identifier for a span",
  },
  {
    key: "span_duration_ms",
    label: "Span Duration",
    type: FilterValueType.Latency,
    operators: NUMERIC_OPERATORS,
    unit: "ms",
    group: "Span",
    aliases: ["span duration"],
    icon: ClockIcon,
    iconClassName: ICON_CLASS,
    description: "Duration of an individual span",
  },
  {
    key: "span_cost_usd",
    label: "Span Cost",
    type: FilterValueType.Cost,
    operators: NUMERIC_OPERATORS,
    unit: "$",
    group: "Span",
    aliases: ["span cost"],
    icon: CurrencyDollarIcon,
    iconClassName: ICON_CLASS,
    description: "Estimated cost of an individual span",
  },
  {
    key: "span_tokens",
    label: "Span Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    unit: "tokens",
    group: "Span",
    aliases: ["span tokens"],
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Total tokens for an individual span",
  },
  {
    key: "span_input_tokens",
    label: "Span Input Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    unit: "tokens",
    group: "Span",
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Input tokens for an individual span",
  },
  {
    key: "span_output_tokens",
    label: "Span Output Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    unit: "tokens",
    group: "Span",
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Output tokens for an individual span",
  },
  {
    key: "span_models_used",
    label: "Models Used in Span",
    type: FilterValueType.String,
    operators: ARRAY_OPERATORS,
    group: "Span",
    aliases: ["span model", "models used in span"],
    suggestions: { source: SuggestionSourceType.Aggregation },
    icon: CubeIcon,
    iconClassName: ICON_CLASS,
    description: "Models used anywhere in the span's subtree",
  },
  {
    key: "span_prompt_id",
    label: "Prompt Template Used in Span",
    type: FilterValueType.Enum,
    operators: ID_OPERATORS,
    group: "Span",
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Prompt template for an individual span",
    suggestions: { source: SuggestionSourceType.Aggregation },
    versionedEntity: SPAN_PROMPT_VERSIONED_ENTITY,
  },
  {
    key: "span_workflow_id",
    label: "Workflow Used in Span",
    type: FilterValueType.Enum,
    operators: ID_OPERATORS,
    group: "Span",
    icon: SwitchHorizontalIcon,
    iconClassName: ICON_CLASS,
    description: "Workflow for an individual span",
    suggestions: { source: SuggestionSourceType.Aggregation },
    versionedEntity: SPAN_WORKFLOW_VERSIONED_ENTITY,
  },
  {
    key: "span_tool_name",
    label: "Tool Used in Span",
    type: FilterValueType.String,
    operators: STRING_OPERATORS,
    group: "Span",
    aliases: ["span tool", "span tool name"],
    suggestions: { source: SuggestionSourceType.Aggregation },
    icon: CursorClickIcon,
    iconClassName: ICON_CLASS,
    description: "Tool name for an individual span",
  },
  {
    key: "span_output_text",
    label: "Span Output Text",
    type: FilterValueType.String,
    operators: TEXT_SEARCH_OPERATORS,
    group: "Span",
    aliases: ["span output", "span output text"],
    icon: ChatAlt2Icon,
    iconClassName: ICON_CLASS,
    description: "Output text produced by a span",
  },
  {
    key: "span_exception_text",
    label: "Span Exception",
    type: FilterValueType.String,
    operators: TEXT_SEARCH_OPERATORS,
    group: "Span",
    aliases: ["exception", "error", "span exception"],
    icon: ExclamationCircleIcon,
    iconClassName: ICON_CLASS,
    description: "Exception message recorded on a span",
  },
  {
    key: SPAN_ATTRIBUTES_FIELD,
    label: "Span Attribute",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Span",
    aliases: ["attribute", "attributes", "span attribute", "span attributes"],
    icon: DatabaseIcon,
    iconClassName: ICON_CLASS,
    description: "Key/value attributes recorded on a span",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "span_attribute_keys",
      valuesField: "span_attribute_values",
    },
  },
  {
    key: "span_resource",
    label: "Span Resource",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Span",
    aliases: ["span resource"],
    icon: ServerIcon,
    iconClassName: ICON_CLASS,
    description: "OpenTelemetry resource attributes on a span",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "span_resource_keys",
      valuesField: "span_resource_values",
    },
  },
  {
    key: "span_input_variables",
    label: "Span Input Variable",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Span",
    aliases: ["variable", "variables", "input variable", "input variables"],
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Input variables passed to a span's prompt template",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "span_input_variable_keys",
      valuesField: "span_input_variable_values",
    },
  },
  {
    key: SPAN_INPUT_FIELD,
    label: "Span Input",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Span",
    aliases: ["function input"],
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Search inside a span's function_input by sub-key (e.g. value)",
  },
  {
    key: SPAN_OUTPUT_FIELD,
    label: "Span Output",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Span",
    aliases: ["function output"],
    icon: ChatAlt2Icon,
    iconClassName: ICON_CLASS,
    description: "Search inside a span's function_output by sub-key (e.g. value)",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "span_output_keys",
      valuesField: "span_output_values",
    },
  },
  {
    key: SPAN_DEPTH_FIELD,
    label: "Span Depth",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Span",
    icon: SwitchHorizontalIcon,
    iconClassName: ICON_CLASS,
    description: "Nesting depth of an individual span",
  },
  {
    key: "span_total_child_count",
    label: "# of Child Spans",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Span",
    icon: SwitchHorizontalIcon,
    iconClassName: ICON_CLASS,
    description: "Number of descendant spans under a span",
  },
  {
    key: "span_is_leaf",
    label: "Span is Leaf",
    type: FilterValueType.Boolean,
    operators: BOOLEAN_OPERATORS,
    group: "Span",
    aliases: ["leaf", "is leaf"],
    icon: LinkIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the span has no child spans",
  },
  {
    key: SPAN_IS_ROOT_FIELD,
    label: "Span is Root",
    type: FilterValueType.Boolean,
    operators: BOOLEAN_OPERATORS,
    group: "Span",
    aliases: ["root", "is root", "span is root"],
    icon: LinkIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the span is the root of the trace",
  },
];

const compileVersionedEntityTraceFilter = (
  filter: Filter,
  config: VersionedEntityConfig,
): StructuredFilterRequest | StructuredFilterGroupRequest => {
  const compileSingle = (value: VersionedEntityFilterValue): StructuredFilterRequest => {
    const entityId = getVersionedEntityId(value);
    const versionNumber = getVersionedEntityVersionNumber(value);

    if (versionNumber === undefined) {
      return {
        field: filter.field,
        operator: filter.operator,
        value: String(entityId),
      };
    }

    const versionRef = buildVersionRef(entityId, versionNumber);
    const isSpanLevel = filter.field.startsWith("span_");
    const { operator: filterOperator } = filter;
    let operator = filterOperator;
    if (!isSpanLevel && filterOperator !== "in") {
      operator = "contains";
    }

    return {
      field: config.versionRefField,
      operator,
      value: versionRef,
    };
  };

  if (isVersionedEntityFilterValueList(filter.value)) {
    const compiled = filter.value.map(compileSingle);
    if (compiled.length === 1) {
      return compiled[0]!;
    }

    return {
      logic: filter.operator === "not_in" ? "AND" : "OR",
      filters: compiled,
    };
  }

  const parsed = isVersionedEntityFilterValue(filter.value)
    ? filter.value
    : parseVersionedEntityFilterValue(config.kind, filter.value as string | object);

  if (parsed) {
    return compileSingle(parsed);
  }

  return {
    field: filter.field,
    operator: filter.operator,
    value: filter.value,
  };
};

const compileTraceFilter = (
  filter: Filter,
): StructuredFilterRequest | StructuredFilterGroupRequest => {
  const definition = TRACE_FILTERS.find(item => item.key === filter.field);
  if (definition?.versionedEntity) {
    return compileVersionedEntityTraceFilter(filter, definition.versionedEntity);
  }

  switch (filter.field) {
    case SPAN_IS_ROOT_FIELD:
      return {
        field: SPAN_DEPTH_FIELD,
        operator: filter.operator === "is_false" ? "gt" : "eq",
        value: ROOT_SPAN_DEPTH,
      };
    case SPAN_TYPE_FIELD:
      return {
        field: SPAN_ATTRIBUTES_FIELD,
        operator: SPAN_TYPE_OPERATOR_TO_NESTED[filter.operator] ?? "key_equals",
        value: filter.value,
        nestedKey: SPAN_NODE_TYPE_KEY,
      };
    case SPAN_INPUT_FIELD:
    case SPAN_OUTPUT_FIELD: {
      const prefix =
        filter.field === SPAN_INPUT_FIELD ? SPAN_INPUT_ATTR_PREFIX : SPAN_OUTPUT_ATTR_PREFIX;
      const subKey = filter.nestedKey?.trim();
      return {
        field: SPAN_ATTRIBUTES_FIELD,
        operator: filter.operator,
        value: filter.value,
        nestedKey: subKey ? `${prefix}.${subKey}` : prefix,
      };
    }
    default:
      return {
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
        ...(filter.nestedKey ? { nestedKey: filter.nestedKey } : {}),
      };
  }
};

const buildTraceDateRangeFilter = ({
  from,
  to,
}: StructuredSearchState["dateRange"]): StructuredFilterRequest => ({
  field: TRACE_DATE_RANGE_FIELD,
  operator: "between",
  value: [from.toISOString(), to.toISOString()],
});

const buildTracesPayload = ({
  filters,
  dateRange,
}: StructuredSearchState): StructuredFilterGroupRequest => ({
  logic: "AND",
  filters: [buildTraceDateRangeFilter(dateRange), ...filters.map(compileTraceFilter)],
});

export const tracesSearchSchema: StructuredSearchSchema = {
  id: "traces",
  placeholder: "Search traces or add filters (e.g. status, duration, span attribute)...",
  compactPlaceholder: "Search or add filters…",
  filters: TRACE_FILTERS,
  globalControls: [
    { type: GlobalSearchControlType.DateRange, field: TRACE_DATE_RANGE_FIELD },
    { type: GlobalSearchControlType.Timezone },
    { type: GlobalSearchControlType.CutoverDate },
  ],
  showGroupHeaders: false,
  buildPayload: buildTracesPayload,
};
